import * as React from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { BLOCKS } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"

import Layout from "../../components/layout"
import SEOComponent from "../../components/seo"
import ImageWithTextOverlayNew from "../../components/imageWithTextOverlayNew"
import FeaturedContent from "../../components/featuredContent"
import FeaturedCollectionFetch from "../../components/featuredCollectionFetch"
import FeaturedLinkLayout from "../../components/featuredLinkLayout"
import FeaturedPromotions from "../../components/featuredPromotions"
import HubSpotForm from "../../components/forms/hubspotForm"
import AccordionSection from "../../components/accordion/accordionSection"
import ChoiceEntry from "../../components/accordion/choiceEntry"

const ContentfulLandingPage = (props) => {
  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => <p className="block is-size-5 is-size-6-mobile">{children}</p>,
      [BLOCKS.HEADING_1]: (node, children) => <h1 className="is-size-3 is-size-4-mobile my-4 has-text-danger is-uppercase">{children}</h1>,
      [BLOCKS.HEADING_2]: (node, children) => <h2 className="is-size-4 is-size-5-mobile my-3 has-text-danger is-uppercase">{children}</h2>,
      [BLOCKS.HEADING_3]: (node, children) => <h3 className="is-size-5 is-size-6-mobile my-2 has-text-danger is-uppercase">{children}</h3>,
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        if (node.data.target.__typename === "ContentfulAccordionChoices") {
          const { choiceTitle, choiceImage, choiceImageAltText, choiceDescription } = node.data.target
          return (
            <ChoiceEntry
              entryName={choiceTitle}
              entryImg={choiceImage.gatsbyImageData}
              entryImgAlt={choiceImageAltText}  
            >
              {renderRichText(choiceDescription)}
            </ChoiceEntry>
          )
        } else if (node.data.target.__typename === "ContentfulFeaturedContent") {
          const { header, destination, textAlignment, content, buttonLink, buttonText } = node?.data?.target
          return (
            <FeaturedContent
              header={header}
              destination={destination}
              textAlignment={textAlignment}
              buttonLink={buttonLink}
              buttonText={buttonText}
            >
              {renderRichText(content, options)}
            </FeaturedContent>
          )
        } else if (node.data.target.__typename === "ContentfulFeaturedCollection") {
          return (
            <FeaturedCollectionFetch
              collection={node.data.target.collection}
              header={node.data.target.header}
              showPrice={node.data.target.showPrice}
            >
              {node.data.target.content && (renderRichText(node.data.target.content))}
            </FeaturedCollectionFetch>
          )
        } else if (node.data.target.__typename === "ContentfulSectionOfFeaturedLinks") {
          return (
            <section className="section">
              <div className="container">
                {node.data.target.content.references.map((el) =>
                  <FeaturedLinkLayout
                    image={el.image.gatsbyImageData}
                    alt={el.alt}
                    heading={el.heading}
                    button={el.button}
                    destination={el.destination}
                    key={el.id}
                  >
                    {renderRichText(el.featuredLinkBody, options)}
                  </FeaturedLinkLayout>
                )}
              </div>
            </section>
          )
        } else if (node.data.target.__typename === "ContentfulSectionOfFeaturedPromotions") {
          return (
            <FeaturedPromotions props={node.data.target.content.references} />
          )
        } else if (node.data.target.__typename === "ContentfulRichTextContentBlock") {
          return (
            <section className="section">
              <div className="container">
                <div className="content">
                  {renderRichText(node.data.target.content, options)}
                </div>
              </div>
            </section>
          )
        } else if (node.data.target.__typename === "ContentfulSectionOfAccordions") {
          return (
            <section className="section">
              <div className="container">
                <dl className="content m-4">
                  {node.data.target.content.references.map((accordion, i) => (
                    <AccordionSection key={i} title={accordion.title}>
                      {renderRichText(accordion.content, options)}
                    </AccordionSection>
                  ))}
                </dl>
              </div>
            </section>
          )
        } else {
          console.log(node.data.target.__typename," is missing from options")
        }
      },
      [BLOCKS.EMBEDDED_ASSET]: node => {
        const { gatsbyImageData, description } = node.data.target
        if (!gatsbyImageData) {
          return null
        }
        return (
          <section className="section">
            <div className="container">
              <GatsbyImage
                image={gatsbyImageData}
                alt={description}
              />
            </div>
          </section>
        )
      },
  
    },
    renderText: text => {
      return text.split('\n').reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment]
      }, [])
    }
  }

  const { hubspotForm } = props.data.contentfulLandingPage
  const { heroImage } = props.data.contentfulLandingPage

  return (
    <Layout>
      <SEOComponent
        title={props.data.contentfulLandingPage.title}
        description={props.data.contentfulLandingPage.description}
      />
      {heroImage &&
        <ImageWithTextOverlayNew
          image={heroImage.image.gatsbyImageData}
          alt={heroImage.imageAltText}
          preheading={heroImage.preheading}
          heading={heroImage.heading}
          subheading={heroImage.subheading}
          solidBackground={heroImage.solidBackground}
          transparentBackground={heroImage.transparentBackground}
          textPosition={heroImage.textPosition}
          textAlignment={heroImage.textAlignment}
          firstButtonLabel={heroImage.firstButtonLabel}
          firstButtonLink={heroImage.firstButtonLink}
          highlightFirstButton={heroImage.highlightFirstButton}
          secondButtonLabel={heroImage.secondButtonLabel}
          secondButtonLink={heroImage.secondButtonLink}
        />
      }
      {renderRichText(props.data.contentfulLandingPage.content, options)}

      {hubspotForm &&
        <section className="section" id={hubspotForm.formId}>
          <div className="container">
            <h2 className="is-size-3 is-size-4-mobile my-4 has-text-centered has-text-danger is-uppercase">
              {hubspotForm.heading}
            </h2>
            {hubspotForm.content && renderRichText(hubspotForm.content, options)}
            <div className="columns is-centered">
              <div className="column is-two-thirds">
                <HubSpotForm
                  bgColor={hubspotForm.bgColor}
                  formName={hubspotForm.formName}
                  pageName={hubspotForm.pageName}
                  location={props.location}
                  formTitle={hubspotForm.formTitle}
                  formSubtitle={hubspotForm.formSubtitle}
                />
              </div>
            </div>
          </div>
        </section>
      }
    </Layout>
  )
}

export default ContentfulLandingPage

export const query = graphql`
  query($id: String) {
    contentfulLandingPage(id: {eq: $id}) {
      id
      slug
      title
      description
      heroImage {
        image {
          gatsbyImageData(layout: FULL_WIDTH)
        }
        imageAltText
        preheading
        heading
        subheading
        solidBackground
        transparentBackground
        textPosition
        textAlignment
        firstButtonLabel
        firstButtonLink
        highlightFirstButton
        secondButtonLabel
        secondButtonLink
        highlightSecondButton
      }
      content {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            __typename
            gatsbyImageData(layout: FULL_WIDTH)
            description
          }
          ... on ContentfulFeaturedCollection {
            contentful_id
            __typename
            collection
            header
            content {
              raw
            }
            showPrice
          }
          ... on ContentfulFeaturedContent {
            id
            contentful_id
            __typename
            header
            destination
            textAlignment
            content {
              raw
            }
            buttonLink
            buttonText
          }
          ... on ContentfulRichTextContentBlock {
            contentful_id
            __typename
            id
            content {
              raw
            }
          }
          ... on ContentfulSectionOfAccordions {
            id
            contentful_id
            __typename
            content {
              raw
              references {
                title
                content {
                  raw
                  references {
                    id
                    contentful_id
                    __typename
                    choiceTitle
                    productType
                    choiceImage {
                      gatsbyImageData
                    }
                    choiceImageAltText
                    choiceDescription {
                      raw
                    }
                  }
                }
              }
            }
          }
          ... on ContentfulSectionOfFeaturedLinks {
            contentful_id
            __typename
            id
            title
            content {
              references {
                id
                title
                heading
                image {
                  gatsbyImageData
                }
                alt
                featuredLinkBody {
                  raw
                }
                button
                destination
              }
            }
          }
          ... on ContentfulSectionOfFeaturedPromotions {
            contentful_id
            __typename
            content {
              references {
                id
                heading
                image {
                  gatsbyImageData
                }
                imageAltText
              }
            }
          }
        }
      }
      hubspotForm {
        formId
        heading
        content {
          raw
        }
        bgColor
        formName
        pageName
        formTitle
        formSubtitle
      }
    }
  }
`